import {
  Customer,
  CustomerBucket,
  CustomerRecordType,
  CustomerStatus,
} from './customerTypes';

export interface ApiCustomer {
  id: string;
  customerId: string;
  name: string;
  type?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactNumber?: string | null;
  hqId?: string | null;
  hqName?: string | null;
  address?: string | null;
  postcode?: string | null;
  country?: string | null;
  operatingHoursFrom?: string | null;
  operatingHoursTo?: string | null;
  notes?: string | null;
  recordType?: string | null;
  market?: string | null;
  channel?: string | null;
  lifecycleStatus?: string | null;
  partnerPayerRef?: string | null;
  partnerBillToRef?: string | null;
  readinessGaps?: number | null;
  hierarchyBucket?: string | null;
  hierarchyGroupName?: string | null;
  hierarchyGroupAvatarCode?: string | null;
  hierarchySubNodeName?: string | null;
  hierarchyParentName?: string | null;
  hierarchyIsShipToSite?: boolean | null;
  hierarchyIsDemandNode?: boolean | null;
  hierarchyBanner?: string | null;
  deletedAt?: string | null;
  isArchived?: boolean | null;
}

const RECORD_TYPES: CustomerRecordType[] = [
  'Customer',
  'Site',
  'GroupingNode',
  'Aggregate',
];

const STATUSES: CustomerStatus[] = [
  'prospect',
  'onboarding',
  'active',
  'onHold',
  'suspended',
  'archived',
];

const toRecordType = (value?: string | null): CustomerRecordType =>
  RECORD_TYPES.includes(value as CustomerRecordType)
    ? (value as CustomerRecordType)
    : 'Customer';

const toStatus = (value?: string | null): CustomerStatus =>
  STATUSES.includes(value as CustomerStatus)
    ? (value as CustomerStatus)
    : 'active';

const toBucket = (value?: string | null): CustomerBucket => {
  if (value === 'group' || value === 'independent') return value;
  return 'unassigned';
};

const typeLabelOf = (recordType: CustomerRecordType): string =>
  recordType === 'Site' ? 'Site' : 'Sold-to';

const toTimeOfDay = (value?: string | null): string =>
  (value ?? '').slice(0, 5);

export const apiCustomerToViewModel = (api: ApiCustomer): Customer => {
  const recordType = toRecordType(api.recordType);
  const gaps = api.readinessGaps ?? 0;
  const isEnriched = !!(
    api.hierarchyBucket ||
    api.market ||
    api.lifecycleStatus
  );
  return {
    id: api.id,
    customerId: api.customerId,
    name: api.name,
    type: api.type ?? '',
    contactName: api.contactName ?? '',
    contactEmail: api.contactEmail ?? '',
    contactNumber: api.contactNumber ?? '',
    hqId: api.hqId ?? '',
    hqName: api.hqName ?? '',
    address: api.address ?? '',
    postcode: api.postcode ?? '',
    country: api.country ?? '',
    operatingHoursFrom: toTimeOfDay(api.operatingHoursFrom),
    operatingHoursTo: toTimeOfDay(api.operatingHoursTo),
    notes: api.notes ?? '',
    recordType,
    typeLabel: typeLabelOf(recordType),
    market: api.market ?? '',
    channel: api.channel ?? null,
    hierarchy: {
      bucket: toBucket(api.hierarchyBucket),
      groupName: api.hierarchyGroupName ?? null,
      groupAvatarCode: api.hierarchyGroupAvatarCode ?? null,
      subNodeName: api.hierarchySubNodeName ?? null,
      parentName: api.hierarchyParentName ?? null,
      isShipToSite: api.hierarchyIsShipToSite ?? false,
      isDemandNode: api.hierarchyIsDemandNode ?? false,
      banner: api.hierarchyBanner ?? null,
    },
    partners: {
      payerRef: api.partnerPayerRef ?? '',
      billToRef: api.partnerBillToRef ?? '',
    },
    readiness: isEnriched ? { ready: gaps === 0, gaps } : null,
    status: toStatus(api.lifecycleStatus),
    isArchived: !!api.isArchived || !!api.deletedAt,
  };
};
